import React, { useState, useEffect } from 'react';
import { KTIcon } from '../../../../_myb/helpers';

interface InlineEditFieldProps {
  value: string;
  name: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  displayComponent?: React.ReactNode;
  displayClassName?: string;
  inputClassName?: string;
  inputType?: 'text' | 'number' | 'email';
  showLabel?: boolean;
  label?: string;
  containerClassName?: string;
}

export const InlineEditField: React.FC<InlineEditFieldProps> = ({
  value,
  name,
  onChange,
  placeholder = 'Enter value'.toTranslatedDisplay(),
  displayComponent,
  displayClassName = 'fw-bolder ms-1 text-primary cursor-pointer',
  inputClassName = 'form-control form-control-lg',
  inputType = 'text',
  showLabel = false,
  label = '',
  containerClassName = ''
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [originalValue, setOriginalValue] = useState(value);

  useEffect(() => {
    setEditValue(value);
    setOriginalValue(value);
  }, [value]);

  const handleEdit = () => {
    setOriginalValue(value);
    setIsEditing(true);
  };

  const handleSave = () => {
    if (editValue !== originalValue) {
      const syntheticEvent = {
        target: {
          name: name,
          value: editValue
        }
      } as React.ChangeEvent<HTMLInputElement>;
      
      onChange(syntheticEvent);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(originalValue);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const handleLocalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditValue(e.target.value);
  };

  if (isEditing) {
    return (
      <div className={`d-flex align-items-center gap-2 ${containerClassName}`}>
        {showLabel && label && (
          <label className="form-label mb-0 fw-bold">{label}</label>
        )}
        <div className="position-relative flex-grow-1" style={{ maxWidth: '400px' }}>
          <input
            type={inputType}
            className={inputClassName}
            placeholder={placeholder}
            name={name}
            value={editValue}
            onChange={handleLocalChange}
            onKeyDown={handleKeyDown}
            autoFocus
            style={{
              paddingRight: '90px',
              fontSize: '1.1rem',
              fontWeight: '500'
            }}
          />
          <div className="position-absolute top-50 end-0 translate-middle-y me-2 d-flex gap-1">
            <button
              type="button"
              className="btn btn-sm btn-icon btn-success"
              onClick={handleSave}
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title={'Save (Enter)'.toTranslatedDisplay()}
              style={{ width: '32px', height: '32px' }}
            >
              <KTIcon iconName="check" className="fs-4" />
            </button>
            <button
              type="button"
              className="btn btn-sm btn-icon"
              onClick={handleCancel}
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title={'Cancel (Esc)'.toTranslatedDisplay()}
              style={{ width: '32px', height: '32px' }}
            >
              <KTIcon iconName="cross" className="fs-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      onClick={handleEdit} 
      className="d-inline-flex align-items-center gap-2 cursor-pointer hover-opacity-75 transition"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleEdit();
        }
      }}
    >
      {showLabel && label && (
        <span className="text-muted fw-semibold">{label}</span>
      )}
      {displayComponent || (
        <h4 className={displayClassName}>{value}</h4>
      )}
      <KTIcon 
        iconName="pencil" 
        className="fs-3 text-muted opacity-75 hover-opacity-100" 
      />
    </div>
  );
};